<template>
  <v-card width="70vw" class="rounded-20 card-bg pa-2">
    <v-card flat class="white rounded-20 mb-5 py-0">
      <v-card-title class="title-font font-weight-medium">
        <v-fade-transition mode="out-in">
          <div :key="scanBodySelectionLabel">
            {{
              !validateScansResponse
                ? `SELECT ${scanBodySelectionLabel.toUpperCase()}`
                : validationPass && validationReconsider
                ? "SCAN ANALYSIS WARNING - CONSIDER RESCAN"
                : validationPass && !validationReconsider
                ? "SCAN ANALYSIS SUCCESS"
                : "SCAN ANALYSIS FAILURE"
            }}
          </div>
        </v-fade-transition>
        <span v-if="retryCount && validationPass === false">
          &nbsp;- Attempt {{ retryCount + 1 }}
        </span>
        <v-spacer />
        <div
          v-if="uploadedScans.length === 3 && validationPass === false"
          class="mr-3"
        >
          &nbsp;<v-icon color="error" class="mr-1">mdi-alert-circle</v-icon>
          Maximum retry count reached.
        </div>
        <v-btn icon @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-subtitle v-if="!validateScansResponse">
        Begin first by selecting the top facet of all the Scan Gauges<span
          v-if="narrowScanBodiesPresent"
        >
          only</span
        >, then confirm.
        <span v-if="narrowScanBodiesPresent">
          Then, select the top facet of the Narrow Scan Bodies, then confirm.
        </span>
      </v-card-subtitle>
    </v-card>
    <v-card-text style="height: 500px" class="white rounded-20 py-0 px-3">
      <v-row class="fill-height" v-show="!validateScansResponse">
        <v-col cols="6" class="fill-height">
          <v-card
            class="fill-width fill-height elevation-0 view-background rounded-10"
          >
            <v-fade-transition mode="out-in">
              <div
                v-if="scanBodySelectionLabel"
                style="position: absolute"
                class="py-2 px-4 title-font"
                :key="scanBodySelectionLabel"
              >
                Select the top facet of {{ scanBodySelectionLabel }}
              </div>
            </v-fade-transition>
            <div ref="scan1View"></div>
          </v-card>
        </v-col>

        <v-col cols="6" class="fill-height">
          <v-card
            class="fill-width fill-height elevation-0 view-background rounded-10"
          >
            <v-fade-transition mode="out-in">
              <div
                v-if="scanBodySelectionLabel"
                style="position: absolute"
                class="py-2 px-4 title-font"
                :key="scanBodySelectionLabel"
              >
                Select the top facet of {{ scanBodySelectionLabel }}
              </div>
            </v-fade-transition>
            <div ref="scan2View"></div>
          </v-card>
        </v-col>
      </v-row>
      <v-row class="fill-height" v-show="validateScansResponse">
        <v-col cols="6" class="fill-width fill-height">
          <v-fade-transition>
            <v-card
              class="fill-width fill-height elevation-0 view-background rounded-10"
            >
              <div ref="scanValidationView"></div>
            </v-card>
          </v-fade-transition>
        </v-col>
        <v-col cols="6" class="fill-width fill-height">
          <v-card class="fill-width fill-height elevation-0">
            <v-card-text>
              <v-row>
                <v-col class="text-center">
                  <div v-if="!validationPass">
                    <v-progress-circular
                      width="10"
                      size="128"
                      color="error"
                      value="100"
                    >
                      <v-icon color="error" size="100">mdi-close</v-icon>
                    </v-progress-circular>
                    <h1 class="mt-5">
                      <span class="error--text">FAIL</span>
                    </h1>
                  </div>
                  <div v-if="validationPass">
                    <v-progress-circular
                      width="10"
                      size="128"
                      :color="validationReconsider ? 'warning' : 'success'"
                      value="100"
                    >
                      <v-icon
                        v-if="!validationReconsider"
                        color="success"
                        size="100"
                      >
                        mdi-check
                      </v-icon>
                      <v-icon
                        v-if="validationReconsider"
                        color="warning"
                        size="100"
                      >
                        mdi-exclamation-thick
                      </v-icon>
                    </v-progress-circular>
                    <h1 class="mt-5">
                      <span v-if="!validationReconsider" class="success--text">
                        PASS
                      </span>
                      <span v-if="validationReconsider" class="warning--text">
                        WARN
                      </span>
                    </h1>
                  </div>
                  <HeatmapScale
                    v-if="showHeatmapColors"
                    class="mt-10 mx-auto"
                  />
                </v-col>
                <v-col>
                  <v-form>
                    <v-switch
                      inset
                      dense
                      label="Heatmap Colors"
                      v-model="showHeatmapColors"
                    />
                    <v-switch
                      inset
                      dense
                      label="Scan Tissue"
                      v-model="showScanTissue"
                    />
                  </v-form>
                </v-col>
              </v-row>
              <v-row v-if="validationReconsider">
                <v-col>
                  <v-card-text class="text-left">
                    <h3 class="black--text mb-4">Consider Rescanning</h3>
                    <p>
                      The Scan Validation process has determined that the scans
                      provided may produce inaccurate results. This is a warning
                      only, the case may continue at your discretion.
                    </p>
                    <p>
                      If you believe the inaccuracy is due to mis-selected Scan
                      Body faces, please redo the validation process. If not,
                      new scans will ne needed, OR, continue with the case as
                      is.
                    </p>
                  </v-card-text>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-actions class="white rounded-20 mt-2 py-3">
      <template v-if="!validateScansResponse">
        <v-btn class="btn-primary mr-4" @click="select" :disabled="loading">
          Confirm Selections
        </v-btn>
        <v-btn
          class="rounded-10 elevation-0 mr-auto"
          @click="clearSelections"
          :disabled="loading"
        >
          Clear All Selections
        </v-btn>
        <v-scale-transition origin="center center">
          <div class="flex-grow-1 ml-3" v-if="loading">
            <v-progress-linear
              rounded
              indeterminate
              color="primary"
              height="36"
              class="rounded-10"
              background-opacity="0.6"
            >
              <span
                class="text-body-2 font-weight-medium white--text loading-pulse"
              >
                PROCESSING...
              </span>
            </v-progress-linear>
          </div>
        </v-scale-transition>

        <div class="ml-3" v-if="retrying && faceCount">
          Expected Gauge Selection Count: <strong> {{ faceCount }} </strong>
          <v-btn small @click="restart" class="ml-3 elevation-0 rounded-10">
            Restart
          </v-btn>
        </div>
      </template>
      <template v-if="validateScansResponse">
        <v-btn
          :class="[
            'mr-2 rounded-10 elevation-0 px-5',
            validationReconsider
              ? 'warning'
              : validationPass
              ? 'success'
              : 'error'
          ]"
          @click="$emit('close')"
        >
          {{ validationPass ? "Finish" : "Close" }}
        </v-btn>
        <template v-if="!validationPass">
          <v-btn
            class="btn-primary mr-2"
            :disabled="!retryPermitted"
            @click="onRetry"
          >
            Retry Analysis
          </v-btn>
          <v-btn class="rounded-10 elevation-0" @click="restart">
            Restart Session
          </v-btn>
        </template>
      </template>
    </v-card-actions>
  </v-card>
</template>

<style scoped>
.view-background {
  background: linear-gradient(
    to right bottom,
    #cbd7df 0%,
    #eeefee 67%,
    rgba(255, 249, 234, 0.25)
  );
}
</style>

<script>
import { mapGetters } from "vuex"
import * as NCFrontend from "@osteon-nexus-connect-lib/nc-frontend"
import apiClient from "@/lib/ApiClient"
import HeatmapScale from "@/components/shared/HeatmapScale.vue"

export default {
  scan1View: null,
  scan2View: null,
  scanValidationView: null,

  props: [
    "value",
    "retrying",
    "ncSessionId",
    "addScansResponse",
    "uploadedScans",
    "validationPass",
    "validationReconsider",
    "validateScansDialog",
    "narrowScanBodiesPresent",
    "retryCount",
    "immediate"
  ],

  components: {
    HeatmapScale
  },

  data() {
    return {
      loading: false,
      showScanTissue: true,
      showHeatmapColors: false,
      selectGaugesResponse: null,
      scanBodySelectionLabel: "Scan Gauges",
      faceCount: null,
      scanViewReloads: 0
    }
  },

  computed: {
    ...mapGetters(["token"]),

    validateScansResponse: {
      get() {
        return this.value
      },
      set(value) {
        return this.$emit("input", value)
      }
    },

    retryPermitted() {
      return this.uploadedScans.length < 3
    }
  },

  watch: {
    retrying(value) {
      if (value) {
        this.scan1View.clearVisualisation()
        this.scan2View.clearVisualisation()
        this.scanValidationView.clearVisualisation()
        this.selectGaugesResponse = null
      }
    },

    ncSessionId(value) {
      if (value === null) this.restart()
    },

    showHeatmapColors() {
      this.toggleShowHeatmapColors()
    },

    showScanTissue(value) {
      if (this.scanValidationView && this.scanValidationView.visualisation) {
        this.scanValidationView.visualisation.setScanTissueVisible(value)
      }
    },

    validateScansDialog(value) {
      if (value && this.retrying && this.scanViewReloads < this.retryCount) {
        this.setScanVisualisations()
        this.scanViewReloads++
      }
    }
  },

  mounted() {
    this.scan1View = NCFrontend.createView("scan1View", this.$refs.scan1View)
    this.scan2View = NCFrontend.createView("scan2View", this.$refs.scan2View)
    this.scanValidationView = NCFrontend.createView(
      "scanValidationView",
      this.$refs.scanValidationView
    )
    NCFrontend.add(this.scan1View, this.scan2View, this.scanValidationView)
    this.setScanVisualisations()
  },

  unmounted() {
    NCFrontend.remove(this.scan1View, this.scan2View, this.scanValidationView)
  },

  methods: {
    async setScanVisualisations() {
      const visualisations = await Promise.all([
        NCFrontend.Visualisations.ScanBodySelect.make(this.scan1View.element, {
          mesh: this.addScansResponse.scan1.mesh,
          detectedPlanesForScanGauges:
            this.addScansResponse.scan1.detectedPlanesForScanGauges,
          detectedPlanesForNarrowScanBodies:
            this.addScansResponse.scan1.detectedPlanesForNarrowScanBodies
        }),
        NCFrontend.Visualisations.ScanBodySelect.make(this.scan2View.element, {
          mesh: this.addScansResponse.scan2.mesh,
          detectedPlanesForScanGauges:
            this.addScansResponse.scan2.detectedPlanesForScanGauges,
          detectedPlanesForNarrowScanBodies:
            this.addScansResponse.scan2.detectedPlanesForNarrowScanBodies
        })
      ])
      this.scan1View.setVisualisation(visualisations[0])
      this.scan2View.setVisualisation(visualisations[1])
    },

    async select() {
      const scan1Visualisation = this.scan1View.visualisation
      const scan2Visualisation = this.scan2View.visualisation

      const scan1SelectedPlaneIndicesForScanGauges =
        scan1Visualisation.selectedPlaneIndicesForScanGauges
      const scan2SelectedPlaneIndicesForScanGauges =
        scan2Visualisation.selectedPlaneIndicesForScanGauges
      if (
        scan1SelectedPlaneIndicesForScanGauges.length === 0 ||
        scan2SelectedPlaneIndicesForScanGauges.length === 0
      ) {
        this.$emit("error", "No Scan Gauges have been selected.")
        return
      }
      if (
        scan1SelectedPlaneIndicesForScanGauges.length !==
        scan2SelectedPlaneIndicesForScanGauges.length
      ) {
        this.$emit("error", "Mismatching number of Scan Gauges selected.")
        return
      }

      let scan1SelectedPlaneIndicesForNarrowScanBodies = []
      let scan2SelectedPlaneIndicesForNarrowScanBodies = []
      if (this.narrowScanBodiesPresent) {
        if (
          scan1Visualisation.selectingNarrowScanBodies &&
          scan2Visualisation.selectingNarrowScanBodies
        ) {
          scan1SelectedPlaneIndicesForNarrowScanBodies =
            scan1Visualisation.selectedPlaneIndicesForNarrowScanBodies
          scan2SelectedPlaneIndicesForNarrowScanBodies =
            scan2Visualisation.selectedPlaneIndicesForNarrowScanBodies
          if (
            scan1SelectedPlaneIndicesForNarrowScanBodies.length === 0 ||
            scan2SelectedPlaneIndicesForNarrowScanBodies.length === 0
          ) {
            this.$emit("error", "No Narrow Scan Bodies have been selected.")
            return
          }
          if (
            scan1SelectedPlaneIndicesForNarrowScanBodies.length !==
            scan2SelectedPlaneIndicesForNarrowScanBodies.length
          ) {
            this.$emit(
              "error",
              "Mismatching number of Narrow Scan Bodies selected."
            )
            return
          }
        } else {
          scan1Visualisation.startSelectionForNarrowScanBodies()
          scan2Visualisation.startSelectionForNarrowScanBodies()
          this.scanBodySelectionLabel = "Narrow Scan Bodies"
          return
        }
      }

      if (
        this.faceCount !== null &&
        scan1SelectedPlaneIndicesForScanGauges.length +
          scan1SelectedPlaneIndicesForNarrowScanBodies.length !==
          this.faceCount
      ) {
        this.$emit(
          "error",
          `Selection Mismatch: Must select ${this.faceCount} Scan Bodies`
        )
        return
      }

      try {
        this.loading = true
        scan1Visualisation.stopSelection()
        scan2Visualisation.stopSelection()
        const selectGaugesResponse =
          await NCFrontend.ApiClient.sessionSelectGauges(
            `${apiClient.instanceUrl}/${apiClient.tenantUid}/validation`,
            this.$axios.defaults.headers.Authorization,
            {
              sessionId: this.ncSessionId,
              scan1Id: this.addScansResponse.scan1.id,
              scan2Id: this.addScansResponse.scan2.id,
              scan1SelectedPlaneIndicesForScanGauges,
              scan1SelectedPlaneIndicesForNarrowScanBodies,
              scan2SelectedPlaneIndicesForScanGauges,
              scan2SelectedPlaneIndicesForNarrowScanBodies
            }
          )
        await Promise.all([
          scan1Visualisation.showSelectedScanBodies(
            selectGaugesResponse.scan1.scanGauges,
            selectGaugesResponse.scan1.narrowScanBodies
          ),
          scan2Visualisation.showSelectedScanBodies(
            selectGaugesResponse.scan2.scanGauges,
            selectGaugesResponse.scan2.narrowScanBodies
          )
        ])
        this.showSelectedScanBodies = null
        this.selectGaugesResponse = selectGaugesResponse
      } catch (err) {
        this.setErrorMessage("An internal error occurred.")
        console.error(err)
      } finally {
        this.loading = false
      }
      this.validateScans()
    },

    async validateScans() {
      try {
        this.loading = true
        const validateScansResponse =
          await NCFrontend.ApiClient.sessionValidateScans(
            `${apiClient.instanceUrl}/${apiClient.tenantUid}/validation`,
            this.$axios.defaults.headers.Authorization,
            {
              sessionId: this.ncSessionId,
              scan1Id: this.addScansResponse.scan1.id,
              scan2Id: this.addScansResponse.scan2.id,
              retrying: this.retrying
            }
          )
        this.validateScansResponse = validateScansResponse
        if (this.faceCount === null) {
          const visualisation1 = NCFrontend.get("scan1View").visualisation
          const gaugeCount =
            visualisation1.selectedPlaneIndicesForScanGauges?.length || 0
          const narrowCount =
            visualisation1.scan1SelectedPlaneIndicesForNarrowScanBodies
              ?.length || 0
          this.faceCount = gaugeCount + narrowCount
        }
        this.$emit("retrying", false)
        this.$nextTick(() => {
          this.scanValidationView.setVisualisation(
            new NCFrontend.Visualisations.ScanValidation(
              this.$refs.scanValidationView,
              {
                fixedScan: this.validateScansResponse.fixedScan,
                scanAnalysis: this.validateScansResponse.scanAnalysis,
                immediateCase: this.immediate
              }
            )
          )
        })
      } catch (err) {
        this.$emit("error", "An internal error occurred.")
        console.error(err)
      } finally {
        this.loading = false
      }
    },

    toggleShowHeatmapColors() {
      if (!this.showHeatmapColors) {
        this.scanValidationView.visualisation.showScoreColors()
      } else {
        this.scanValidationView.visualisation.showHeatmapColors()
      }
    },

    reselect() {
      this.scanValidationView.clearVisualisation()
      this.validateScansResponse = null
      this.selectGaugesResponse = null
      this.scan1View.visualisation.reset()
      this.scan2View.visualisation.reset()
      this.scan1View.visualisation.clearSelections()
      this.scan2View.visualisation.clearSelections()
      this.scan1View.visualisation.startSelectionForScanGauges()
      this.scan2View.visualisation.startSelectionForScanGauges()
      this.scanBodySelectionLabel = "Scan Gauges"
    },

    clearSelections() {
      this.scan1View.visualisation.clearSelections()
      this.scan2View.visualisation.clearSelections()
      this.scan1View.visualisation.startSelectionForScanGauges()
      this.scan2View.visualisation.startSelectionForScanGauges()
      this.scan1View.visualisation.clearSelections()
      this.scan2View.visualisation.clearSelections()
      this.scanBodySelectionLabel = "Scan Gauges"
    },

    onRetry() {
      this.scan1View.clearVisualisation()
      this.scan2View.clearVisualisation()
      this.scanValidationView.clearVisualisation()
      this.selectGaugesResponse = null
      this.$emit("retrying", true)
      this.$emit("close")
    },

    restart() {
      this.reselect()
      this.selectGaugesResponse = null
      this.showScanTissue = true
      this.showHeatmapColors = false
      this.scanViewReloads = 0
      this.faceCount = null
      if (this.ncSessionId) this.$emit("restart")
    }
  }
}
</script>
